import { Stack } from '@ethings/ui-toolkit';
import { useSnackbar } from 'notistack';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isAppliedFilterArrayEqual } from '../../util/filter';
import { AppliedFilter, SavedFilterOptions } from '../entity-search/types';
import { SavedFilterElement } from '../filter';

interface Props {
  filters: AppliedFilter[];
  filterOptions: SavedFilterOptions;
  onFiltersChanged: (filters: AppliedFilter[]) => unknown;
  savedFilter: SavedFilter[];
  setSavedFilter: Dispatch<SetStateAction<SavedFilter[]>>;
}

export interface SavedFilter {
  created: number;
  filters: AppliedFilter[];
  id: string;
  name: string;
}

interface FilterStore {
  all: SavedFilter[];
  removeById(id: string): unknown;
  renameById(id: string, name: string): boolean;
}

interface ViewProps {
  filters: AppliedFilter[];
  onFiltersChanged: (filters: AppliedFilter[]) => unknown;
  store: FilterStore;
}

export const SAVED_FILTER_KEY = 'saved-filters' as const;

export const SavedFilterView: FC<ViewProps> = ({ filters, onFiltersChanged, store }) => {
  return (
    <Stack spacing={2}>
      {store.all.map(({ id, name, filters: savedFilters }) => (
        <SavedFilterElement
          key={id}
          filters={savedFilters}
          id={id}
          isApplied={isAppliedFilterArrayEqual(savedFilters, filters)}
          name={name}
          onLoadFilter={() => onFiltersChanged(savedFilters)}
          onRemoveFilter={() => store.removeById(id)}
          onRenameFilter={(newName: string) => store.renameById(id, newName)}
        />
      ))}
    </Stack>
  );
};

export const NewSavedFilterController: FC<Props> = ({ filters, onFiltersChanged, savedFilter, setSavedFilter }) => {
  const { t } = useTranslation(['common']);
  const { enqueueSnackbar } = useSnackbar();

  const sortedFilters = useMemo(
    () => savedFilter ? savedFilter.sort((a, b) => b.created - a.created) : [],
    [savedFilter],
  );

  const removeById = (id: string): void => {
    setSavedFilter(savedFilter.filter((item) => item.id !== id));
  };

  const renameById = (id: string, name: string): boolean => {
    const trimmedName = name.trim();
    const duplicate = savedFilter.some((item) => item.id !== id && item.name === trimmedName);
    if (duplicate) {
      enqueueSnackbar(t('common:component.filter.hint.duplicate-name'), { variant: 'warning' });
      return false;
    }
    setSavedFilter(savedFilter.map((item) => item.id !== id ? item : { ...item, name: trimmedName }));
    return true;
  };

  return (
    <SavedFilterView
      filters={filters}
      onFiltersChanged={onFiltersChanged}
      store={{
        all: sortedFilters,
        removeById,
        renameById,
      }}
    />
  );
};
